import React from 'react';
import { View, Text, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { Colors, FontSize, Radii, Space, FontFamily } from '../theme/tokens';

/**
 * SKTICK-DB-001 Surface C — node display.
 *
 * `node_id` is an OPAQUE reference to an SK Platform node. SK Tickets does not
 * resolve the hierarchy and MUST NOT APPEAR TO:
 *
 *   • It is a label, never a link and never a breadcrumb. A breadcrumb would
 *     promise a tree this product cannot walk.
 *   • It falls back to the raw id when no label was passed, rather than
 *     hiding — an unlabelled outlet is still the outlet the ticket is about.
 *
 * The label, when present, rides on the ticket payload from whoever raised it.
 */

interface Props {
  nodeId: string | null | undefined;
  /** Human label passed on the payload. Falls back to the raw id. */
  nodeLabel?: string | null;
  style?: StyleProp<ViewStyle>;
}

export function NodeLabel({ nodeId, nodeLabel, style }: Props) {
  if (!nodeId) {
    return null;
  }
  const text = nodeLabel?.trim() || nodeId;
  const isRawId = !nodeLabel?.trim();
  return (
    <View style={[styles.wrap, style]}>
      <Text
        style={[styles.text, isRawId && styles.raw]}
        numberOfLines={1}
        // Not a link: no accessibilityRole="link", no press handler. There is
        // nowhere in this product for it to go.
      >
        {text}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    paddingVertical: 2,
    paddingHorizontal: Space[2],
    borderRadius: Radii.r2,
    backgroundColor: Colors.paper3,
    alignSelf: 'flex-start',
    maxWidth: 220,
  },
  text: {
    fontSize: FontSize.xs,
    color: Colors.ink2,
    fontFamily: FontFamily.sans,
  },
  /** A bare id is machine text, so it reads as one. */
  raw: {
    fontFamily: FontFamily.mono,
    color: Colors.ink3,
  },
});
